import { Fragment } from "react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { BarChart3 } from "lucide-react";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";

import { chartColors } from "@/lib/chart";
import { message, formatSnakeCase } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { CompetitorPlatformComparisonAPIData } from "@/schema/brand-os/brand-intel";

interface PlatformData {
  platform: string;
  brands: Record<string, number>;
}

function buildPlatformData(raw: CompetitorPlatformComparisonAPIData): PlatformData[] {
  const platforms = raw.platform_comparison;
  if (!platforms) return [];
  return Object.entries(platforms).map(([key, entries]) => {
    const brands: Record<string, number> = {};
    for (const entry of entries) {
      for (const [brand, count] of Object.entries(entry)) {
        if (typeof count === "number") brands[brand] = (brands[brand] ?? 0) + count;
      }
    }
    return {
      platform: formatSnakeCase(key),
      brands,
    };
  });
}

function ChartView({ data }: { data: PlatformData[] }) {
  if (!data.length) return <LockedContent message={message} className="mt-10" />;

  const allBrands = Array.from(new Set(data.flatMap((item) => Object.keys(item.brands))));
  const chartConfig: ChartConfig = {};
    allBrands.forEach((brand, i) => {
      chartConfig[brand] = { label: brand, color: chartColors[i % chartColors.length] };
    });
    const chartData = data.map((item) => ({ platform: item.platform, ...item.brands }));

  return (
    <Fragment>
      <ChartContainer config={chartConfig} className="w-full h-64">
        <BarChart accessibilityLayer data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
          <XAxis dataKey="platform" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "var(--muted-foreground)", fontSize: 12 }} />
          <YAxis tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "var(--muted-foreground)", fontSize: 12 }} />
          <ChartTooltip cursor={false} content={<ChartTooltipContent className="font-semibold min-w-44" />} />
          {allBrands.map((brand) => (
            <Bar key={brand} dataKey={brand} name={brand} fill={chartConfig[brand]?.color} radius={[3, 3, 0, 0]} barSize={20} />
          ))}
        </BarChart>
      </ChartContainer>
      <div className="flex items-center justify-center gap-5 border-t border-border pt-4 mt-4">
        {allBrands.map((brand) => (
          <div key={brand} className="flex items-center gap-1.5">
            <div className="size-2 shrink-0 rounded-full" style={{ backgroundColor: chartConfig[brand]?.color }} aria-hidden />
            <p className="text-sm text-muted-foreground">{brand}</p>
          </div>
        ))}
      </div>
    </Fragment>
  );
}

function ChartContent({ status, data }: { status: QueryStatus; data: PlatformData[] }) {
  switch (status) {
    case "pending":
      return <ChartSkeleton />;
    case "error":
      return <LockedContent message={message} className="mt-10" />;
    case "success":
      return <ChartView data={data} />;
  }
}

export function ContentOutputChart() {
  const now = new Date();
  const { data: apiData, status } = useQuery(
    BrandIntelApiFactory.Queries.FetchCompetitorComparison({ month: now.getMonth() + 1, year: now.getFullYear() })
  );

  const data = apiData ? buildPlatformData(apiData) : [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-blue-100/40 p-2">
            <BarChart3 className="size-5 text-blue-600" strokeWidth={2.5} />
          </div>
          <CardTitle className="text-base font-semibold">Content Output by Platform</CardTitle>
        </div>
        {status === "pending" ? <Skeleton className="h-4 w-28" /> : data.length > 0 && <p className="text-xs text-muted-foreground">Monthly · assets/mo</p>}
      </CardHeader>
      <CardContent className="flex-1 pt-16">
        <ChartContent status={status} data={data} />
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <div className="w-full h-64 flex items-end gap-3">
      {[50, 30, 65, 40].map((h, i) => (
        <div key={i} className="flex-1 flex items-end gap-1">
          <Skeleton className="flex-1 rounded-t-md" style={{ height: `${h}%` }} />
          <Skeleton className="flex-1 rounded-t-md" style={{ height: `${h * 0.6}%` }} />
        </div>
      ))}
    </div>
  );
}
